import { useCallback, useEffect, useRef, useState } from 'react';
import { authSync, AuthEventType } from '../../common/publicUtils';

const INACTIVITY_MS = 30 * 60 * 1000; // 30 minutes
const WARNING_MS = 2 * 60 * 1000;     // 2 minutes

interface UseInactivityTimeoutOptions {
  onForcedLogout: () => void;
}

interface UseInactivityTimeoutReturn {
  showModal: boolean;
  expirationTime: number | null;
  onKeepAlive: () => void;
  onLogoutNow: () => void;
  /** Call from handleAuthEvent for cross-tab events */
  handleSyncEvent: (type: string) => void;
}

export function useInactivityTimeout({
  onForcedLogout,
}: UseInactivityTimeoutOptions): UseInactivityTimeoutReturn {
  const inactivityTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const warningTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [expirationTime, setExpirationTime] = useState<number | null>(null);
  const isModalShowingRef = useRef(false);

  // Stable ref so timer callbacks always call the latest onForcedLogout
  const onForcedLogoutRef = useRef(onForcedLogout);
  useEffect(() => { onForcedLogoutRef.current = onForcedLogout; }, [onForcedLogout]);

  const showWarning = useCallback(() => {
    if (isModalShowingRef.current) return;
    setShowModal(true);
    isModalShowingRef.current = true;
    setExpirationTime(Date.now() + WARNING_MS);
    warningTimerRef.current = setTimeout(() => onForcedLogoutRef.current(), WARNING_MS);
  }, []);

  const resetTimers = useCallback(() => {
    if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
    if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
    inactivityTimerRef.current = setTimeout(() => {
      const hasToken =
        !!localStorage.getItem('JMLAuthToken') || !!localStorage.getItem('JMLPublicToken');
      if (!hasToken) return;
      authSync.broadcast({ type: 'SESSION_TIMEOUT_WARNING' });
      showWarning();
    }, INACTIVITY_MS);
  }, [showWarning]);

  const lastBroadcastRef = useRef<number>(0);
  const handleUserActivity = useCallback(() => {
    if (isModalShowingRef.current) return;
    const hasToken =
      !!localStorage.getItem('JMLAuthToken') || !!localStorage.getItem('JMLPublicToken');
    if (!hasToken) {
      if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
      if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
      return;
    }

    // Reset local timers
    resetTimers();

    // Broadcast to other tabs to reset their timers, throttled to once every 30s
    const now = Date.now();
    if (now - lastBroadcastRef.current > 30000) {
      lastBroadcastRef.current = now;
      authSync.broadcast({ type: 'USER_ACTIVITY' });
    }
  }, [resetTimers]);

  // Stable ref for cross-tab event handler
  const handleUserActivityRef = useRef(handleUserActivity);
  useEffect(() => { handleUserActivityRef.current = handleUserActivity; }, [handleUserActivity]);

  const onKeepAlive = useCallback(() => {
    if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
    setShowModal(false);
    isModalShowingRef.current = false;
    authSync.broadcast({ type: 'SESSION_TIMEOUT_KEEPALIVE' });
    handleUserActivity();
  }, [handleUserActivity]);

  const onLogoutNow = useCallback(() => {
    if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
    if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
    setShowModal(false);
    isModalShowingRef.current = false;
    onForcedLogoutRef.current();
  }, []);

  /** Handle cross-tab sync events from handleAuthEvent */
  const handleSyncEvent = useCallback((type: AuthEventType) => {
    if (type === 'USER_ACTIVITY') {
      handleUserActivityRef.current();
    } else if (type === 'SESSION_TIMEOUT_WARNING') {
      showWarning();
    } else if (type === 'SESSION_TIMEOUT_KEEPALIVE') {
      if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
      setShowModal(false);
      isModalShowingRef.current = false;
      handleUserActivityRef.current();
    }
  }, [showWarning]);

  const events = ['load', 'mousemove', 'mousedown', 'click', 'scroll', 'keypress'];

  useEffect(() => {
    events.forEach((e) => window.addEventListener(e, handleUserActivity));
    handleUserActivity();
    return () => {
      events.forEach((e) => window.removeEventListener(e, handleUserActivity));
      if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
      if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleUserActivity]);

  return { showModal, expirationTime, onKeepAlive, onLogoutNow, handleSyncEvent };
}
